import is from '@sindresorhus/is';
import type { FunctionComponent } from 'react';
import { Fragment } from 'react';
import type { Control } from 'react-hook-form';
import { useWatch } from 'react-hook-form';

import { useTranslate } from '~/shared/context/use-translate';
import type { JobProfileComponent, SectionTagDefinition } from '~/shared/data-contract/job-profile';

import { Section, SectionHeader } from '../components/Common';
import { DescriptionSectionSkeleton } from '../components/SkeletonLoaders';
import { DescriptionMarkStates } from './comments/DescriptionMarkStates';
import { DescriptionSection, EditableDescriptionSection } from './EditableDescription';
import type { RegenerationState } from './RegenerateDialog';
import type { ProfileForm } from './types';
import { useTranslatedSectionTitle } from './use-translated-section-title';

type StructuredContentSectionProps = {
    readonly activeLanguageIndex: number;
    readonly commentable: boolean;
    readonly control: Control<ProfileForm>;
    readonly isEditing: boolean;
    readonly isGeneratingTranslations: boolean;
    readonly itemId: string;
    readonly newThreadCreationDisabled?: boolean;
    readonly onUserInput: () => void;
    readonly overrideSectionTags?: readonly SectionTagDefinition[];
    readonly profile: JobProfileComponent;
    readonly regenerationState: RegenerationState;
};

export const StructuredContentSection: FunctionComponent<StructuredContentSectionProps> = ({
    activeLanguageIndex,
    commentable,
    control,
    isEditing,
    isGeneratingTranslations,
    itemId,
    newThreadCreationDisabled = false,
    onUserInput,
    overrideSectionTags,
    profile,
    regenerationState,
}) => {
    const translate = useTranslate();
    const resolveTitle = useTranslatedSectionTitle(overrideSectionTags ?? profile.sectionTags);
    const watchedStructured = useWatch({ control, name: `translations.${activeLanguageIndex}.structured` });

    if (profile.structured === null) {
        return null;
    }

    const originalStructuredItems = profile.translations[activeLanguageIndex]?.structured ?? profile.structured ?? [];

    return (
        <Section data-testid="structured-content-section">
            {commentable ? <DescriptionMarkStates itemId={itemId} /> : null}
            <SectionHeader>{translate('pages.job-architecture-v2.job-profile-title')}</SectionHeader>
            {regenerationState.includes('CONTENT') ||
            (isGeneratingTranslations && is.falsy(profile.translations?.[activeLanguageIndex]?.structured)) ? (
                <Fragment>
                    <DescriptionSectionSkeleton />
                    <DescriptionSectionSkeleton />
                    <DescriptionSectionSkeleton />
                </Fragment>
            ) : (
                (isEditing
                    ? (watchedStructured ?? profile.translations[activeLanguageIndex]?.structured)
                    : profile.translations[activeLanguageIndex]?.structured
                )?.map((structuredItem, index) => {
                    const displayTitle = resolveTitle(structuredItem);
                    const sectionKey = structuredItem.id ?? structuredItem.tag;
                    const originalStructuredItem = originalStructuredItems.find(
                        (item) => (item.id ?? item.tag) === sectionKey,
                    );
                    const commentMarkedHtml =
                        commentable && newThreadCreationDisabled ? originalStructuredItem?.value : undefined;
                    return (
                        <div key={sectionKey}>
                            {isEditing ? (
                                <EditableDescriptionSection
                                    key={activeLanguageIndex}
                                    activeLanguageIndex={activeLanguageIndex}
                                    commentable={commentable}
                                    commentMarkedHtml={commentMarkedHtml}
                                    control={control}
                                    index={index}
                                    isGenerating={false}
                                    itemId={itemId}
                                    sectionKey={sectionKey}
                                    sectionTitle={displayTitle}
                                    onUserInput={onUserInput}
                                />
                            ) : (
                                <DescriptionSection
                                    commentable={commentable}
                                    itemId={itemId}
                                    sectionKey={sectionKey}
                                    title={displayTitle}
                                    value={structuredItem.value}
                                />
                            )}
                        </div>
                    );
                })
            )}
        </Section>
    );
};
